import { ExpansionPanel, Sidebar } from "@chatscope/chat-ui-kit-react";
import { useFindMany } from "@gadgetinc/react";
import { Icon, LegacyStack, Tag, Text } from "@shopify/polaris";
import { PhoneMajor } from "@shopify/polaris-icons";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { Badge } from "../externalComponents/tailwind/badge";
import SelectInput from "../externalComponents/tailwind/select-input";
import {
  DialogManageConsent,
  optinColors,
  optinLabels,
} from "../externalPages/contacts/shared";
import { CustomerContext } from "../providers";
import { PencilIcon } from "@heroicons/react/24/solid";
import { Button } from "../externalComponents/tailwind/button";

export function CustomerDetailsSidebar({ conversation, isMobile }) {
  const { customer } = useContext(CustomerContext);
  const shopifyCustomer = conversation.contact?.shopifyCustomer;
  const dateOptions = { year: "numeric", month: "long", day: "numeric" };
  const [optinStatus, setOptinStatus] = useState("neverOptin");
  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);

  const [{ data: users }] = useFindMany(api.user, {
    select: {
      id: true,
      firstName: true,
      lastName: true,
    },
  });

  const optionUsers = users
    ? users.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      }))
    : [];

  const [selectedUser, setSelectedUser] = useState(
    conversation?.attributedUser?.id,
  );

  useEffect(() => {
    setSelectedUser(conversation?.attributedUser?.id);
  }, [conversation]);

  const selectedUserLabel = optionUsers.find(
    (user) => user.value === selectedUser,
  )?.label;

  const handleChangeAttributedUser = (value) => {
    api.conversation.update(conversation.id, {
      attributedUser: { _link: value },
    });
  };

  useEffect(() => {
    if (!conversation?.contact) {
      return;
    }
    const { hasOptIn, optoutAt } = conversation.contact;
    setOptinStatus(hasOptIn ? "optin" : optoutAt ? "optout" : "neverOptin");
  }, [conversation]);

  const [tags, setTags] = useState([]);
  const findTags = async (contactId) => {
    const contactTags = await api.contactTag.findMany({
      filter: {
        contactId: { equals: contactId },
      },
      select: {
        id: true,
        contact: {
          id: true,
        },
        tag: {
          id: true,
          name: true,
        },
      },
    });

    const tags = contactTags.map((contactTag) => contactTag?.tag?.name);
    setTags(tags);
  };

  useEffect(() => {
    findTags(conversation?.contact?.id);
  }, [conversation]);

  const handleConsentSuccess = (newConsent) => {
    // Update the optin status based on the new consent
    if (newConsent === "optin") {
      setOptinStatus("optin");
      // Fake changes to update the consent.
      conversation.contact.hasOptIn = true;
    } else if (newConsent === "optout") {
      setOptinStatus("optout");
      // Fake changes to update the consent.
      conversation.contact.hasOptIn = false;
      conversation.contact.optoutAt = new Date().toISOString();
    }
  };

  return (
    <Sidebar
      key="right"
      position="right"
      style={{
        minWidth: isMobile ? "100%" : "300px",
        width: isMobile ? "100%" : "20vw",
      }}
    >
      {!customer.shouldHidePersonalData && (
        <ExpansionPanel open title="CONTACT">
          <div className="flex justify-between items-center">
            <Text variant="headingSm">
              <Link
                to={`/contacts/${conversation?.contact?.id}`}
                className="hover:text-pink hover:underline"
              >
                {conversation?.contact?.whatsappName ??
                  (shopifyCustomer?.firstName ?? "") +
                    " " +
                    (shopifyCustomer?.lastName ?? "")}
              </Link>
            </Text>
            <div className="flex items-center gap-2">
              <Badge color={optinColors[optinStatus]}>
                {optinLabels[optinStatus]}
              </Badge>

              <Button
                outline
                onClick={() => setIsConsentDialogOpen(true)}
                className="h-5"
              >
                <PencilIcon />
              </Button>
            </div>
          </div>
          <LegacyStack>
            <Icon source={PhoneMajor} color="base" />
            <Text>+{conversation.contact?.userPhoneNumber}</Text>
          </LegacyStack>
        </ExpansionPanel>
      )}

      <ExpansionPanel open title="ATTRIBUTED TO">
        <div className="mb-2">
          <SelectInput
            placeholder={
              selectedUser === undefined
                ? "Assign to a user"
                : selectedUserLabel
            }
            value={selectedUser}
            onChange={(value) => {
              setSelectedUser(value);
              handleChangeAttributedUser(value);
            }}
            options={optionUsers}
            selected={selectedUser}
          />
        </div>
      </ExpansionPanel>

      <ExpansionPanel open title="SIMIO DATA">
        <div className="flex justify-between items-center w-full">
          <Text variant="headingSm">Tags</Text>
        </div>

        {tags && Object.keys(tags).length > 0 ? (
          <div
            className="flex flex-wrap my-2 w-full"
            style={{ width: isMobile ? "80vw" : "100%" }}
          >
            {tags.map((tag, index) => (
              <div key={`contactTag-${index}`}>
                <Badge className="mr-1 mb-1">{tag}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <Text>No tags for this contact</Text>
        )}

        <div className="flex justify-between items-center">
          <Text variant="headingSm">Custom properties</Text>
        </div>

        {conversation?.contact?.customProperties &&
        Object.keys(conversation.contact.customProperties).length > 0 ? (
          <div>
            {Object.keys(conversation.contact.customProperties).map((key) => (
              <div key={key}>
                {/* <Text variant="bodyMd">
                    {key}: {conversation.contact.customProperties[key]}
                  </Text> */}

                <InformationLine
                  attribute={key}
                  value={conversation.contact.customProperties[key]}
                />
              </div>
            ))}
          </div>
        ) : (
          <Text>No custom properties for this contact</Text>
        )}
      </ExpansionPanel>

      {shopifyCustomer && (
        <>
          <ExpansionPanel open title="SHOPIFY">
            <Link
              removeunderline="true"
              onClick={() =>
                window.open(
                  `https://admin.shopify.com/store/${
                    shopifyCustomer.shop.myshopifyDomain.split(
                      ".myshopify.com",
                    )[0]
                  }/customers/${shopifyCustomer.id}`,
                  "_blank",
                )
              }
            >
              <Text variant="headingMd">
                View {shopifyCustomer.firstName} {shopifyCustomer.lastName}
              </Text>
            </Link>

            <InformationLine
              attribute={"Created"}
              value={new Date(shopifyCustomer.shopifyCreatedAt).toLocaleString(
                "fr-FR",
                dateOptions,
              )}
            />
            <InformationLine
              attribute={"Total spent"}
              value={Math.round(shopifyCustomer.amountSpent?.amount ?? shopifyCustomer.totalSpent ?? 0) + "€"}
            />
            <InformationLine
              attribute={"Orders"}
              value={shopifyCustomer.numberOfOrders ?? shopifyCustomer.ordersCount}
            />
          </ExpansionPanel>

          {shopifyCustomer.lastOrder && (
            <ExpansionPanel open title="LAST ORDER">
              <Text variant="headingMd">{shopifyCustomer.lastOrder?.name}</Text>
              <LegacyStack>
                <Tag>
                  {shopifyCustomer.lastOrder.financialStatus?.toUpperCase()}
                </Tag>
                <Tag>
                  {shopifyCustomer.lastOrder.fulfillmentStatus?.toUpperCase() ??
                    "UNFULFILLED"}
                </Tag>
              </LegacyStack>
              <InformationLine
                attribute={"Created"}
                value={new Date(
                  shopifyCustomer.lastOrder.shopifyCreatedAt,
                ).toLocaleString("fr-FR", dateOptions)}
              />
              <InformationLine
                attribute={"Total"}
                value={Math.round(shopifyCustomer.lastOrder.totalPrice) + "€"}
              />
            </ExpansionPanel>
          )}

          {shopifyCustomer.lastOrder?.shippingAddress && (
            <ExpansionPanel open title="ADDRESS">
              <InformationLine
                attribute={"Name"}
                value={shopifyCustomer.lastOrder.shippingAddress?.name}
              />
              <InformationLine
                attribute={"Address 1"}
                value={shopifyCustomer.lastOrder.shippingAddress?.address1}
              />
              <InformationLine
                attribute={"Address 2"}
                value={shopifyCustomer.lastOrder.shippingAddress?.address2}
              />
              <InformationLine
                attribute={"City"}
                value={shopifyCustomer.lastOrder.shippingAddress?.city}
              />
              <InformationLine
                attribute={"Country"}
                value={shopifyCustomer.lastOrder.shippingAddress?.country}
              />
              <InformationLine
                attribute={"Zip code"}
                value={shopifyCustomer.lastOrder.shippingAddress?.zip}
              />
            </ExpansionPanel>
          )}
        </>
      )}

      <DialogManageConsent
        currentConsent={conversation?.contact}
        contactNumber={conversation?.contact?.userPhoneNumber}
        isModalOpen={isConsentDialogOpen}
        onClose={() => setIsConsentDialogOpen(false)}
        onSuccess={handleConsentSuccess}
      />
    </Sidebar>
  );
}

function InformationLine({ attribute, value }) {
  return (
    <LegacyStack>
      <Text variant="bodyMd">{attribute}</Text>
      <Text variant="bodyMd" fontWeight="bold">
        {value}
      </Text>
    </LegacyStack>
  );
}
